import { useMemo } from 'react'
import { isEmpty, isNonNullish } from 'remeda'
import { useNewsletter } from '@velasca/hooks/use-newsletter'
import { ResponsivePicture } from '@velasca/ui/responsive-picture'
import { buildIntrinsicWidthSources } from '@velasca/utils/build-intrinsic-width-sources'
import { getMediaSources } from '@velasca/utils/get-media-sources'
import { setQueryParams } from '@velasca/utils/set-query-params'
import { NewsletterForm } from './NewsletterForm/NewsletterForm'
import {
  Background,
  BackgroundContainer,
  FormWrapper,
  ImageWrapper,
  Layout,
} from './NewsletterSection.styles'
import type { NewsletterSectionProps } from './NewsletterSection.types'
import { SuccessMessage } from './SuccessMessage/SuccessMessage'

export const NewsletterSection = ({
  title,
  description,
  desktopImage,
  mobileImage,
  backgroundImage,
  placeholder,
  successMessage,
  resubcribeButtonText,
  privacyPolicyText,
  errorMessage,
}: NewsletterSectionProps): JSX.Element | null => {
  const { status, setStatus, subscribe } = useNewsletter()
  const mediaSources = useMemo(() => {
    return (
      mobileImage && desktopImage && getMediaSources(mobileImage, desktopImage)
    )
  }, [mobileImage, desktopImage])
  const fallbackImage = mediaSources ? mediaSources[0] : null

  if (!title) {
    return null
  }

  return (
    <BackgroundContainer>
      {backgroundImage && (
        <Background
          lazyload
          lqip
          imgSrc={setQueryParams(backgroundImage.src, [
            { name: 'width', value: '30' },
          ])}
          sources={buildIntrinsicWidthSources({
            src: backgroundImage.src,
          })}
        />
      )}
      <Layout>
        <FormWrapper>
          {status === 'success' ? (
            <SuccessMessage
              setStatus={setStatus}
              message={successMessage}
              buttonText={resubcribeButtonText}
            />
          ) : (
            <NewsletterForm
              status={status}
              setStatus={setStatus}
              subscribe={subscribe}
              errorMessage={errorMessage}
              title={title}
              description={description}
              placeholder={placeholder}
              privacyPolicyText={privacyPolicyText}
            />
          )}
        </FormWrapper>

        <ImageWrapper>
          {isNonNullish(fallbackImage) && !isEmpty(fallbackImage) && (
            <ResponsivePicture
              lazyload
              lqip
              mediaSources={mediaSources ?? undefined}
              alt={fallbackImage.alt}
              sources={fallbackImage.sources}
              imgSrc={fallbackImage.imgSrc}
            />
          )}
        </ImageWrapper>
      </Layout>
    </BackgroundContainer>
  )
}
